import { useEffect, useState, useContext } from "react";
import { useParams, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { ReportesService } from "../../services/ReportesService";
import Preloader from "../../components/Preloader";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line,
    AreaChart, Area,
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const ReportesDetail = () => {
    const { id } = useParams();
    const { token } = useContext(AuthContext);
    const location = useLocation();
    const reporteInfo = location.state?.reporte;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const limit = reporteInfo?.tipo_reporte === "listado" ? 10 : 100;
                const response = await ReportesService.execute(id, token, currentPage, limit);

                let dataFormateada;

                if (reporteInfo?.tipo_reporte === "listado") {
                    dataFormateada = response.data;
                } else {
                    dataFormateada = response.data.map(item => ({
                        ...item,
                        valor: item.valor ? Number.parseFloat(item.valor) : 0,
                        etiqueta: String(item.etiqueta || '')
                    }));
                }

                setData(dataFormateada);
                if (response.pagination) setPagination(response.pagination);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, token, currentPage]);

    const handleExportExcel = async () => {
        try {
            setExporting(true);
            const blob = await ReportesService.exportToExcel(id, token);

            const url = URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `${reporteInfo?.nombre_reporte || 'reporte'}.xlsx`);

            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Error al descargar:", error);
            alert("No se pudo generar el Excel. Verifica que el reporte tenga datos.");
        } finally {
            setExporting(false);
        }
    };


    const renderChart = () => {
        if (data.length === 0) return null;

        return (
            <div className="chart-container" style={{ width: '100%', height: 500, marginTop: '20px' }}>
                <ResponsiveContainer width="100%" height="100%">

                    {reporteInfo?.tipo_reporte === "barra" ? (
                        <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 60 }} >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                            <XAxis dataKey="etiqueta" angle={-45}
                                textAnchor="end"
                                interval={0}
                                tick={{ fontSize: 12, fill: '#6b7280' }}
                                height={60} />
                            <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />

                            <Bar dataKey="valor" fill="#0d6efd" name={reporteInfo.nombre_reporte} radius={[4, 4, 0, 0]} barSize={40}>
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    ) :

                        reporteInfo?.tipo_reporte === "linea" ? (
                            <LineChart data={data} margin={{ top: 10, right: 30, left: 40, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="etiqueta" />
                                <YAxis tickFormatter={(value) => new Intl.NumberFormat('es-MX', { notation: 'compact' }).format(value)} />
                                <Tooltip formatter={(value) => new Intl.NumberFormat('es-MX').format(value)} />
                                <Legend />
                                <Line type="monotone" dataKey="valor" stroke="#0d6efd" strokeWidth={3} dot={{ r: 4 }} />
                            </LineChart>
                        ) :


                            reporteInfo?.tipo_reporte === "area" ? (
                                <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="etiqueta" />
                                    <YAxis />
                                    <Tooltip />
                                    <Area type="monotone" dataKey="valor" stroke="#0d6efd" fill="#0d6efd" fillOpacity={0.3} />
                                </AreaChart>
                            ) :


                                (reporteInfo?.tipo_reporte === "pie" || reporteInfo?.tipo_reporte === "dona") ? (
                                    <PieChart>
                                        <Pie
                                            data={data}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={reporteInfo?.tipo_reporte === "dona" ? 70 : 0}
                                            outerRadius={120}
                                            paddingAngle={5}
                                            dataKey="valor"
                                            nameKey="etiqueta"
                                            label={({ etiqueta }) => etiqueta}
                                        >
                                            {data.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                ) :


                                    reporteInfo?.tipo_reporte === "radar" ? (
                                        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
                                            <PolarGrid />
                                            <PolarAngleAxis dataKey="etiqueta" />
                                            <PolarRadiusAxis />
                                            <Radar
                                                name={reporteInfo.nombre_reporte}
                                                dataKey="valor"
                                                stroke="#8884d8"
                                                fill="#8884d8"
                                                fillOpacity={0.6}
                                            />
                                            <Tooltip />
                                            <Legend />
                                        </RadarChart>
                                    ) : null}
                </ResponsiveContainer>
            </div>
        );
    };

    const formatCellValue = (val) => {
        if (val === null || val === undefined) return '-';

        if (typeof val === 'string' && val.includes('-') && !isNaN(Date.parse(val))) {
            const fecha = new Date(val);
            if (!isNaN(fecha.getTime())) {
                return fecha.toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }
        }

        return val.toString();
    };


    if (loading) return <Preloader />;

    return (
        <div className="ventas-admin-content">
            <div className="ventas-admin-header">
                <div className="ventas-admin-header-row">
                    <div>
                        <div className="ventas-admin-title">{reporteInfo?.nombre_reporte}</div>
                        <div className="ventas-admin-subtitle">{reporteInfo?.descripcion_reporte}</div>
                    </div>
                    <button
                        className="btn-exportar"
                        onClick={handleExportExcel}
                        disabled={exporting || data.length === 0}
                    >
                        {exporting ? 'Generando...' : 'Exportar a Excel'}
                    </button>
                </div>
            </div>

            <div className="ventas-admin-tab-content">
                <div className="ventas-admin-container">

                    {reporteInfo?.tipo_reporte === "listado" ? (
                        <>
                            <h2 className="ventas-table-title">Registros del Reporte</h2>
                            {data.length > 0 ? (
                                <div className="ventas-table-wrapper">
                                    <table className="ventas-table">
                                        <thead>
                                            <tr>
                                                {Object.keys(data[0]).map((col) => (
                                                    <th key={col}>{col.toUpperCase().replace(/_/g, ' ')}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((fila, i) => (
                                                <tr key={i}>
                                                    {Object.values(fila).map((val, j) => (
                                                        <td key={j}>
                                                            {formatCellValue(val)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="alert alert-info">No se encontraron registros.</div>
                            )}
                        </>
                    ) : (

                        <>
                            <h2 className="ventas-table-title">Visualización Gráfica</h2>
                            {renderChart()}
                        </>
                    )}
                </div>

                {/* PAGINACIÓN SOLO PARA TABLAS */}
                {reporteInfo?.tipo_reporte === "listado" && pagination.totalPaginas > 1 && (
                    <div className="pagination">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>
                            Anterior
                        </button>
                        <span>Página {currentPage} de {pagination.totalPaginas}</span>
                        <button disabled={currentPage === pagination.totalPaginas} onClick={() => setCurrentPage(prev => prev + 1)}>
                            Siguiente
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportesDetail;